import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const typeLabels = { reclamation: '🔴 Réclamation', suggestion: '💡 Suggestion', satisfaction: '⭐ Satisfaction' };
const statutLabels = { en_attente: '⏳ En attente', en_cours: '🔄 En cours', traite: '✅ Traité', ferme: '🔒 Fermé' };
const canalLabels = { oral: '🗣️ Oral', appel: '📞 Appel', mail: '📧 Mail', message: '💬 Message', formulaire: '🌐 Formulaire' };
const graviteLabels = { risque_faible: '🟢 Risque faible', risque_moyen: '🟡 Risque moyen', risque_grand: '🔴 Risque grand' };
const statutColors = {
    en_attente: { bg: 'rgba(217,119,6,0.15)', color: '#fbbf24', border: 'rgba(217,119,6,0.3)' },
    en_cours: { bg: 'rgba(2,132,199,0.15)', color: '#38bdf8', border: 'rgba(2,132,199,0.3)' },
    traite: { bg: 'rgba(5,150,105,0.15)', color: '#34d399', border: 'rgba(5,150,105,0.3)' },
    ferme: { bg: 'rgba(100,116,139,0.15)', color: '#94a3b8', border: 'rgba(100,116,139,0.3)' },
};
const typeColors = {
    reclamation: { bg: 'rgba(239,68,68,0.15)', color: '#f87171', border: 'rgba(239,68,68,0.3)' },
    suggestion: { bg: 'rgba(0,212,255,0.15)', color: '#00d4ff', border: 'rgba(0,212,255,0.3)' },
    satisfaction: { bg: 'rgba(16,185,129,0.15)', color: '#34d399', border: 'rgba(16,185,129,0.3)' },
};

const Badge = ({ type, map, colorMap }) => {
    const c = colorMap[type] || colorMap.ferme || colorMap.suggestion;
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: c.bg, color: c.color, border: `1px solid ${c.border}`, whiteSpace: 'nowrap' }}>{map[type]}</span>;
};

const inputStyle = {
    padding: '10px 14px', background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(0,212,255,0.2)', borderRadius: 10,
    fontSize: 14, color: 'white', fontFamily: 'inherit', outline: 'none',
};

const taStyle = { ...inputStyle, width: '100%', resize: 'vertical', minHeight: 80, boxSizing: 'border-box' };
const selStyle = { ...inputStyle, width: '100%', boxSizing: 'border-box', cursor: 'pointer' };
const inStyle = { ...inputStyle, width: '100%', boxSizing: 'border-box' };

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: 7 };

const MessageModal = ({ message, onClose, onStatusChange, readOnly }) => {
    const [activeTab, setActiveTab] = useState('details');
    const [reponse, setReponse] = useState('');
    const [newNote, setNewNote] = useState('');
    const [newStatut, setNewStatut] = useState(message.statut);
    const [notes, setNotes] = useState(message.notes || []);
    const [loading, setLoading] = useState(false);
    const [analyse, setAnalyse] = useState({
        impact_gravite: message.impact_gravite || '',
        responsable: message.responsable || '',
        cause_analysee: message.cause_analysee || '',
        action_corrective: message.action_corrective || '',
        date_debut: message.date_debut ? message.date_debut.split('T')[0] : '',
        date_cible: message.date_cible ? message.date_cible.split('T')[0] : '',
        resultat_attendu: message.resultat_attendu || '',
        resultat_reel: message.resultat_reel || '',
        amelioration_continue: message.amelioration_continue || '',
    });

    const handleReply = async () => {
        if (!reponse.trim()) return;
        setLoading(true);
        try {
            await api.post(`/messages/${message.id}/reponse`, { contenu: reponse });
            toast.success('Réponse envoyée ✉️');
            setReponse('');
        } catch { toast.error('Erreur'); }
        setLoading(false);
    };

    const handleNote = async () => {
        if (!newNote.trim()) return;
        setLoading(true);
        try {
            await api.post(`/messages/${message.id}/note`, { contenu: newNote });
            setNotes(n => [...n, { id: Date.now(), admin_nom: 'Vous', contenu: newNote, created_at: new Date().toISOString() }]);
            setNewNote('');
            toast.success('Note ajoutée');
        } catch { toast.error('Erreur'); }
        setLoading(false);
    };

    const handleStatut = async () => {
        setLoading(true);
        try {
            await api.patch(`/messages/${message.id}/statut`, { statut: newStatut });
            onStatusChange(message.id, newStatut);
            toast.success('Statut mis à jour');
            onClose();
        } catch { toast.error('Erreur'); }
        setLoading(false);
    };

    const handleAnalyse = async () => {
        setLoading(true);
        try {
            await api.patch(`/messages/${message.id}/analyse`, analyse);
            toast.success('Analyse enregistrée ✅');
        } catch { toast.error('Erreur'); }
        setLoading(false);
    };

    const tabs = readOnly
        ? [['details', '📋 Détails']]
        : [['details', '📋 Détails'], ['analyse', '🔍 Analyse'], ['repondre', '✉️ Répondre'], ['notes', '📝 Notes'], ['statut', '🔄 Statut']];

    return (
        <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.85)', backdropFilter: 'blur(8px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: 'linear-gradient(135deg,#0f2744,#0a1e3a)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 20, width: '100%', maxWidth: 780, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.6)' }}>

                {/* Header */}
                <div style={{ padding: '24px 28px 20px', borderBottom: '1px solid rgba(0,212,255,0.1)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'sticky', top: 0, background: 'linear-gradient(135deg,#0f2744,#0a1e3a)', zIndex: 10 }}>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'white', marginBottom: 4 }}>{message.objet}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#00d4ff' }}>{message.reference}</div>
                    </div>
                    <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', cursor: 'pointer', fontSize: 16, color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                </div>

                <div style={{ padding: '24px 28px' }}>
                    {/* Badges */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                        <Badge type={message.type_message} map={typeLabels} colorMap={typeColors} />
                        <Badge type={message.statut} map={statutLabels} colorMap={statutColors} />
                        {message.origine_canal && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)' }}>
                                {canalLabels[message.origine_canal] || message.origine_canal}
                            </span>
                        )}
                        {message.impact_gravite && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>
                                {graviteLabels[message.impact_gravite]}
                            </span>
                        )}
                    </div>

                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(0,212,255,0.1)', marginBottom: 24, overflowX: 'auto' }}>
                        {tabs.map(([id, label]) => (
                            <button key={id} onClick={() => setActiveTab(id)} style={{ padding: '10px 16px', border: 'none', background: 'none', fontSize: 13, fontWeight: 600, color: activeTab === id ? '#00d4ff' : 'rgba(255,255,255,0.4)', cursor: 'pointer', borderBottom: `2px solid ${activeTab === id ? '#00d4ff' : 'transparent'}`, marginBottom: -1, fontFamily: 'inherit', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>{label}</button>
                        ))}
                    </div>

                    {/* Onglet Détails */}
                    {activeTab === 'details' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                                {[
                                    ['Nom', `${message.prenom} ${message.nom}`],
                                    ['Email', message.email],
                                    ['Téléphone', message.telephone || '—'],
                                    ['Date', new Date(message.created_at).toLocaleDateString('fr-FR') + ' ' + new Date(message.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })],
                                    ['Canal', canalLabels[message.origine_canal] || '—'],
                                    ['Responsable', message.responsable || '—'],
                                ].map(([l, v]) => (
                                    <div key={l}>
                                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.5)', marginBottom: 4 }}>{l}</div>
                                        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>{v}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.5)', marginBottom: 8 }}>Message</div>
                            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 10, padding: 16, fontSize: 14, lineHeight: 1.7, color: 'rgba(255,255,255,0.7)', whiteSpace: 'pre-wrap', marginBottom: 16 }}>{message.message}</div>
                            {message.cause_analysee && (
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.5)', marginBottom: 6 }}>Cause analysée</div>
                                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 10, padding: 14, fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{message.cause_analysee}</div>
                                </div>
                            )}
                            {message.action_corrective && (
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.5)', marginBottom: 6 }}>Action corrective</div>
                                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 10, padding: 14, fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{message.action_corrective}</div>
                                </div>
                            )}
                            {(message.date_debut || message.date_cible) && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
                                    {message.date_debut && <div>
                                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.5)', marginBottom: 4 }}>Date de début</div>
                                        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>{new Date(message.date_debut).toLocaleDateString('fr-FR')}</div>
                                    </div>}
                                    {message.date_cible && <div>
                                        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.5)', marginBottom: 4 }}>Date cible</div>
                                        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>{new Date(message.date_cible).toLocaleDateString('fr-FR')}</div>
                                    </div>}
                                </div>
                            )}
                            {message.resultat_attendu && (
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.5)', marginBottom: 6 }}>Résultat attendu / KPI</div>
                                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 10, padding: 14, fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{message.resultat_attendu}</div>
                                </div>
                            )}
                            {message.resultat_reel && (
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.5)', marginBottom: 6 }}>Résultat réel / Retour</div>
                                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 10, padding: 14, fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{message.resultat_reel}</div>
                                </div>
                            )}
                            {message.amelioration_continue && (
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.5)', marginBottom: 6 }}>Amélioration continue</div>
                                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 10, padding: 14, fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{message.amelioration_continue}</div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Onglet Analyse */}
                    {activeTab === 'analyse' && !readOnly && (
                        <div>
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>Impact / Gravité</label>
                                <select style={selStyle} value={analyse.impact_gravite} onChange={e => setAnalyse(a => ({ ...a, impact_gravite: e.target.value }))}>
                                    <option value="">— Sélectionner —</option>
                                    <option value="risque_faible">🟢 Risque faible</option>
                                    <option value="risque_moyen">🟡 Risque moyen</option>
                                    <option value="risque_grand">🔴 Risque grand</option>
                                </select>
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>Responsable</label>
                                <input style={inStyle} value={analyse.responsable} onChange={e => setAnalyse(a => ({ ...a, responsable: e.target.value }))} placeholder="Nom du responsable" />
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>Cause analysée</label>
                                <textarea style={taStyle} value={analyse.cause_analysee} onChange={e => setAnalyse(a => ({ ...a, cause_analysee: e.target.value }))} placeholder="Quelle est la cause du problème ?" rows="3"></textarea>
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>Action corrective / Amélioration proposée</label>
                                <textarea style={taStyle} value={analyse.action_corrective} onChange={e => setAnalyse(a => ({ ...a, action_corrective: e.target.value }))} placeholder="Quelle action a été mise en place ?" rows="3"></textarea>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                                <div>
                                    <label style={labelStyle}>Date de début</label>
                                    <input type="date" style={inStyle} value={analyse.date_debut} onChange={e => setAnalyse(a => ({ ...a, date_debut: e.target.value }))} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Date cible</label>
                                    <input type="date" style={inStyle} value={analyse.date_cible} onChange={e => setAnalyse(a => ({ ...a, date_cible: e.target.value }))} />
                                </div>
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>Résultat attendu / KPI</label>
                                <textarea style={taStyle} value={analyse.resultat_attendu} onChange={e => setAnalyse(a => ({ ...a, resultat_attendu: e.target.value }))} placeholder="Quel résultat espérez-vous ?" rows="2"></textarea>
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>Résultat réel / Retour</label>
                                <textarea style={taStyle} value={analyse.resultat_reel} onChange={e => setAnalyse(a => ({ ...a, resultat_reel: e.target.value }))} placeholder="Quel a été le résultat réel ?" rows="2"></textarea>
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <label style={labelStyle}>Amélioration continue</label>
                                <textarea style={taStyle} value={analyse.amelioration_continue} onChange={e => setAnalyse(a => ({ ...a, amelioration_continue: e.target.value }))} placeholder="Leçons apprises, suivi après résolution..." rows="2"></textarea>
                            </div>
                            <button onClick={handleAnalyse} disabled={loading} style={{ background: 'linear-gradient(135deg,#1e6fe0,#00d4ff)', border: 'none', borderRadius: 10, padding: '12px 24px', fontSize: 14, fontWeight: 700, color: 'white', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 0 20px rgba(0,212,255,0.2)' }}>
                                💾 Enregistrer l'analyse
                            </button>
                        </div>
                    )}

                    {/* Onglet Répondre */}
                    {activeTab === 'repondre' && !readOnly && (
                        <div>
                            <div style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#60d4ff', display: 'flex', alignItems: 'center', gap: 8 }}>
                                ✉️ Réponse envoyée par email à {message.email}
                            </div>
                            <textarea style={taStyle} rows="5" value={reponse} onChange={e => setReponse(e.target.value)} placeholder="Rédigez votre réponse..."></textarea>
                            <button onClick={handleReply} disabled={!reponse.trim() || loading} style={{ marginTop: 12, background: 'linear-gradient(135deg,#1e6fe0,#00d4ff)', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600, color: 'white', cursor: 'pointer', fontFamily: 'inherit' }}>Envoyer</button>
                        </div>
                    )}

                    {/* Onglet Notes */}
                    {activeTab === 'notes' && (
                        <div>
                            {notes.length === 0 && <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>Aucune note interne</div>}
                            {notes.map(n => (
                                <div key={n.id} style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 10, padding: 14, marginBottom: 10 }}>
                                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>{n.admin_nom} · {new Date(n.created_at).toLocaleDateString('fr-FR')}</div>
                                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>{n.contenu}</div>
                                </div>
                            ))}
                            {!readOnly && (
                                <>
                                    <textarea style={{ ...taStyle, marginTop: 12 }} rows="3" value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Note interne..."></textarea>
                                    <button onClick={handleNote} disabled={!newNote.trim() || loading} style={{ marginTop: 10, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontFamily: 'inherit' }}>Ajouter</button>
                                </>
                            )}
                        </div>
                    )}

                    {/* Onglet Statut */}
                    {activeTab === 'statut' && !readOnly && (
                        <div>
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>Nouveau statut</label>
                                <select style={selStyle} value={newStatut} onChange={e => setNewStatut(e.target.value)}>
                                    <option value="en_attente">⏳ En attente</option>
                                    <option value="en_cours">🔄 En cours</option>
                                    <option value="traite">✅ Traité</option>
                                    <option value="ferme">🔒 Fermé</option>
                                </select>
                            </div>
                            {newStatut === 'traite' && (
                                <div style={{ background: 'rgba(5,150,105,0.1)', border: '1px solid rgba(5,150,105,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#34d399', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    ✅ Email automatique envoyé au client
                                </div>
                            )}
                            <button onClick={handleStatut} disabled={loading} style={{ background: 'linear-gradient(135deg,#1e6fe0,#00d4ff)', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600, color: 'white', cursor: 'pointer', fontFamily: 'inherit' }}>Enregistrer</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const MessagesPage = ({ filterType, readOnly }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
    const [filterStatut, setFilterStatut] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [selectedMsg, setSelectedMsg] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 15 });
            if (filterType) params.append('type', filterType);
            if (filterStatut) params.append('statut', filterStatut);
            if (search) params.append('recherche', search);
            const { data } = await api.get(`/messages?${params}`);
            setMessages(data.messages);
            setPagination(data.pagination);
        } catch { toast.error('Erreur chargement'); }
        setLoading(false);
    }, [page, filterType, filterStatut, search]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const handleOpenDetail = async (msg) => {
        try {
            const { data } = await api.get(`/messages/${msg.id}`);
            setSelectedMsg({ ...data.message, notes: data.notes, reponses: data.reponses });
        } catch { toast.error('Erreur'); }
    };

    const handleStatusChange = (id, statut) => {
        setMessages(msgs => msgs.map(m => m.id === id ? { ...m, statut } : m));
    };

    const titles = { undefined: 'Tous les messages', reclamation: 'Réclamations', suggestion: 'Suggestions', satisfaction: 'Satisfactions' };

    return (
        <div>
            {readOnly && (
                <div style={{ background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 8 }}>
                    👁️ Mode lecture seule — Vous pouvez consulter les messages mais pas les modifier
                </div>
            )}
            <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: 16, overflow: 'hidden' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(0,212,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'white' }}>{titles[filterType] || titles['undefined']}</div>
                        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{pagination.total} résultat(s)</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <input type="text" style={{ ...inputStyle, width: 200 }} placeholder="🔍 Rechercher..." value={searchInput} onChange={e => setSearchInput(e.target.value)} />
                        <select style={{ ...inputStyle, cursor: 'pointer' }} value={filterStatut} onChange={e => { setFilterStatut(e.target.value); setPage(1); }}>
                            <option value="">Tous les statuts</option>
                            <option value="en_attente">⏳ En attente</option>
                            <option value="en_cours">🔄 En cours</option>
                            <option value="traite">✅ Traité</option>
                            <option value="ferme">🔒 Fermé</option>
                        </select>
                    </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                            <div style={{ width: 36, height: 36, border: '3px solid rgba(0,212,255,0.2)', borderTopColor: '#00d4ff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
                            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                        </div>
                    ) : messages.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Aucun message</div>
                            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.3)' }}>Modifiez vos filtres</div>
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {['Référence', 'Client', 'Type', 'Canal', 'Objet', 'Gravité', 'Statut', 'Date', ''].map(h => (
                                        <th key={h} style={{ textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0,212,255,0.6)', borderBottom: '1px solid rgba(0,212,255,0.1)', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {messages.map(m => {
                                    const tc = typeColors[m.type_message] || typeColors.suggestion;
                                    const sc = statutColors[m.statut] || statutColors.ferme;
                                    return (
                                        <tr key={m.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.05)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                            <td style={{ padding: '14px 16px' }}>
                                                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: '#00d4ff', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)', padding: '3px 8px', borderRadius: 6 }}>{m.reference}</span>
                                            </td>
                                            <td style={{ padding: '14px 16px' }}>
                                                <div style={{ fontWeight: 600, color: 'white', fontSize: 13 }}>{m.prenom} {m.nom}</div>
                                                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{m.email}</div>
                                            </td>
                                            <td style={{ padding: '14px 16px' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: tc.bg, color: tc.color, border: `1px solid ${tc.border}` }}>{typeLabels[m.type_message]}</span>
                                            </td>
                                            <td style={{ padding: '14px 16px', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{canalLabels[m.origine_canal] || '—'}</td>
                                            <td style={{ padding: '14px 16px', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>{m.objet}</td>
                                            <td style={{ padding: '14px 16px', fontSize: 12, color: m.impact_gravite === 'risque_grand' ? '#f87171' : m.impact_gravite === 'risque_moyen' ? '#fbbf24' : '#34d399' }}>
                                                {m.impact_gravite ? graviteLabels[m.impact_gravite] : '—'}
                                            </td>
                                            <td style={{ padding: '14px 16px' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{statutLabels[m.statut]}</span>
                                            </td>
                                            <td style={{ padding: '14px 16px', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{new Date(m.created_at).toLocaleDateString('fr-FR')}<br />{new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</td>
                                            <td style={{ padding: '14px 16px' }}>
                                                <button onClick={() => handleOpenDetail(m)} style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, color: '#00d4ff', cursor: 'pointer', fontFamily: 'inherit' }}>👁️ Voir</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {pagination.pages > 1 && (
                    <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(0,212,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>Page {pagination.page}/{pagination.pages}</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid rgba(0,212,255,0.2)', background: 'rgba(0,212,255,0.05)', cursor: 'pointer', fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>‹</button>
                            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(n => (
                                <button key={n} onClick={() => setPage(n)} style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${page === n ? '#00d4ff' : 'rgba(0,212,255,0.2)'}`, background: page === n ? 'rgba(0,212,255,0.2)' : 'rgba(0,212,255,0.05)', cursor: 'pointer', fontSize: 13, fontWeight: page === n ? 700 : 400, color: page === n ? '#00d4ff' : 'rgba(255,255,255,0.6)' }}>{n}</button>
                            ))}
                            <button disabled={page === pagination.pages} onClick={() => setPage(p => p + 1)} style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid rgba(0,212,255,0.2)', background: 'rgba(0,212,255,0.05)', cursor: 'pointer', fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>›</button>
                        </div>
                    </div>
                )}
            </div>
            {selectedMsg && <MessageModal message={selectedMsg} onClose={() => setSelectedMsg(null)} onStatusChange={handleStatusChange} readOnly={readOnly} />}
        </div>
    );
};

export default MessagesPage;